import { CheckCircle2, ShieldCheck } from "lucide-react";
import { useEffect, useState } from "react";
import { formatDateTime } from "@/components/clients/RodoStatusBadges";
import { Alert, Button, Card, Checkbox, Spinner } from "@/components/common";
import { useConfirmPublicConsent, usePublicConsent } from "@/hooks/useClients";
import type { PublicConsent, PublicConsentItem } from "@/types/clients";

/** The token is the credential — keep it out of Referer headers for anything this page loads. */
function useNoReferrer() {
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "referrer";
    meta.content = "no-referrer";
    document.head.appendChild(meta);
    return () => {
      meta.remove();
    };
  }, []);
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-wash px-4 py-8 sm:py-12">
      <div className="mx-auto max-w-2xl space-y-6">
        <div className="flex justify-center">
          <img
            src="/logos/adara-logo.svg"
            alt="Adara Ubezpieczenia"
            className="h-10"
          />
        </div>
        {children}
        <p className="text-center text-xs text-faint">
          ADARA UBEZPIECZENIA SP. Z O.O. · ul. Pogodna 7, 46-022 Luboszyce · KRS
          0001136056 · RAU 11260776/A · iod@adaraubezpieczenia.pl
        </p>
      </div>
    </div>
  );
}

function ItemText({ item }: { item: PublicConsentItem }) {
  return <span className="text-sm leading-relaxed text-sub">{item.text}</span>;
}

function ConfirmedView({ consent }: { consent: PublicConsent }) {
  const rows: [string, boolean][] = [
    ["Baza klientów", consent.clientBase],
    ["Marketing — telefon", consent.marketingPhone],
    ["Marketing — SMS", consent.marketingSms],
    ["Marketing — e-mail", consent.marketingEmail],
  ];
  if (consent.health !== null) {
    rows.push(["Dane o zdrowiu", consent.health]);
  }
  return (
    <Card>
      <div className="space-y-4 text-center">
        <CheckCircle2
          size={48}
          className="mx-auto text-green-600"
          aria-hidden
        />
        <h1 className="text-xl font-semibold text-main">
          Dziękujemy — zapisano
        </h1>
        <p className="text-sm text-sub">
          {consent.confirmedAt && formatDateTime(consent.confirmedAt)} ·
          klauzula w wersji {consent.clauseVersion} · agent {consent.agentName}
        </p>
      </div>
      <ul className="mt-6 divide-y divide-edge-soft rounded-lg border border-edge-soft">
        {rows.map(([label, granted]) => (
          <li key={label} className="flex justify-between px-4 py-2.5 text-sm">
            <span className="text-main">{label}</span>
            <strong className={granted ? "text-green-700" : "text-red-700"}>
              {granted ? "TAK" : "NIE"}
            </strong>
          </li>
        ))}
      </ul>
      <p className="mt-4 text-sm text-sub">
        Potwierdzenie wysłaliśmy SMS-em (i e-mailem, jeśli go podano). Każdą
        zgodę możesz wycofać w dowolnym momencie, pisząc na
        iod@adaraubezpieczenia.pl lub przez swojego agenta.
      </p>
    </Card>
  );
}

function ConsentForm({
  token,
  consent,
}: {
  token: string;
  consent: PublicConsent;
}) {
  const confirm = useConfirmPublicConsent(token);
  const [acknowledged, setAcknowledged] = useState(false);
  const [clientBase, setClientBase] = useState(false);
  const [phone, setPhone] = useState(false);
  const [sms, setSms] = useState(false);
  const [email, setEmail] = useState(false);
  const [health, setHealth] = useState(false);

  const item = (key: PublicConsentItem["key"]) =>
    consent.items.find((i) => i.key === key);
  const confirmation = item("confirmation");
  const base = item("clientBase");
  const marketing = item("marketing");
  const healthItem = item("health");

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    confirm.mutate({
      informationAcknowledged: acknowledged,
      clientBase,
      marketingPhone: phone,
      marketingSms: sms,
      marketingEmail: email,
      health: healthItem ? health : null,
    });
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Card>
        <div className="flex items-start gap-3">
          <ShieldCheck
            size={28}
            className="shrink-0 text-adara-600"
            aria-hidden
          />
          <div>
            <h1 className="text-xl font-semibold text-main">
              Zgoda na przetwarzanie danych osobowych
            </h1>
            <p className="mt-1 text-sm text-sub">
              {consent.clientDisplayName}, Twój agent{" "}
              <strong>{consent.agentName}</strong>
              {consent.agentRauNumber && ` (RAU ${consent.agentRauNumber})`},
              działający w imieniu ADARA UBEZPIECZENIA SP. Z O.O., prosi o
              potwierdzenie zapoznania się z klauzulą informacyjną i o Twoją
              decyzję w sprawie zgód. Link jest ważny do{" "}
              {formatDateTime(consent.expiresAt)}.
            </p>
          </div>
        </div>
      </Card>

      <Card>
        <h2 className="mb-3 text-base font-semibold text-main">
          Klauzula informacyjna (wersja {consent.clauseVersion})
        </h2>
        <div className="max-h-96 overflow-y-auto whitespace-pre-line rounded-lg border border-edge-soft bg-subtle p-4 text-sm leading-relaxed text-sub">
          {consent.informationText}
        </div>
      </Card>

      <Card>
        <div className="space-y-5">
          {confirmation && (
            <div className="space-y-1">
              <Checkbox
                label="Potwierdzam (wymagane)"
                checked={acknowledged}
                onChange={(e) => {
                  setAcknowledged(e.target.checked);
                }}
              />
              <div className="pl-6">
                <ItemText item={confirmation} />
              </div>
            </div>
          )}

          <p className="border-t border-edge-soft pt-4 text-sm text-sub">
            Poniższe zgody są <strong>dobrowolne</strong> — ich brak nie wpływa
            na możliwość otrzymania oferty. Zaznacz tylko te, na które się
            zgadzasz.
          </p>

          {base && (
            <div className="space-y-1">
              <Checkbox
                label={base.label}
                checked={clientBase}
                onChange={(e) => {
                  setClientBase(e.target.checked);
                }}
              />
              <div className="pl-6">
                <ItemText item={base} />
              </div>
            </div>
          )}

          {marketing && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-main">{marketing.label}</p>
              <ItemText item={marketing} />
              <div className="flex flex-wrap gap-4 pt-1">
                <Checkbox
                  label="telefon"
                  checked={phone}
                  onChange={(e) => {
                    setPhone(e.target.checked);
                  }}
                />
                <Checkbox
                  label="SMS"
                  checked={sms}
                  onChange={(e) => {
                    setSms(e.target.checked);
                  }}
                />
                <Checkbox
                  label="e-mail"
                  checked={email}
                  onChange={(e) => {
                    setEmail(e.target.checked);
                  }}
                />
              </div>
            </div>
          )}

          {healthItem && (
            <div className="space-y-1">
              <Checkbox
                label={healthItem.label}
                checked={health}
                onChange={(e) => {
                  setHealth(e.target.checked);
                }}
              />
              <div className="pl-6">
                <ItemText item={healthItem} />
              </div>
            </div>
          )}

          {confirm.isError && (
            <Alert variant="error">
              {(confirm.error as { message?: string }).message ??
                "Nie udało się zapisać decyzji"}
            </Alert>
          )}

          <Button
            type="submit"
            fullWidth
            size="lg"
            isLoading={confirm.isPending}
            disabled={!acknowledged}
          >
            Zapisz moje decyzje
          </Button>
        </div>
      </Card>
    </form>
  );
}

export function PublicConsentPage({ token }: { token: string }) {
  useNoReferrer();
  const { data, isLoading, isError } = usePublicConsent(token);

  if (isLoading) {
    return (
      <Shell>
        <div className="flex justify-center py-24">
          <Spinner size="lg" />
        </div>
      </Shell>
    );
  }

  if (isError || !data) {
    return (
      <Shell>
        <Alert variant="error" title="Link jest nieprawidłowy">
          Sprawdź, czy link został skopiowany w całości, albo poproś agenta o
          wysłanie nowego.
        </Alert>
      </Shell>
    );
  }

  if (data.status === "Confirmed") {
    return (
      <Shell>
        <ConfirmedView consent={data} />
      </Shell>
    );
  }

  if (data.status !== "Pending" || new Date(data.expiresAt) <= new Date()) {
    return (
      <Shell>
        <Alert variant="warning" title="Link wygasł">
          Ten link nie jest już aktywny. Poproś swojego agenta ({data.agentName}
          ) o wysłanie nowego SMS-a.
        </Alert>
      </Shell>
    );
  }

  return (
    <Shell>
      <ConsentForm token={token} consent={data} />
    </Shell>
  );
}
